/**
 * Phase 6c.1 — `usePlanDayCompletion` hook.
 *
 * Toggles a `plan_days.completion_status` between 'completed' and
 * 'upcoming' via the existing backend endpoint
 * `PUT /api/plans/:planId/days/:dayId` (api/index.js:3407).
 *
 * **Why this hook exists:** before this PR, React's `/v2/today` had
 * no UI to log session completions. Athletes could see the planned
 * week but could not mark days as done — the only way to update
 * `plan_days.completion_status` was via the vanilla `plan-actions.js`
 * surface, which retires at Phase 7 cutover. Without this hook, the
 * cutover would silently drop a core feature.
 *
 * **Knock-on:** PR 4e's `computeCurrentStreak` and
 * `computeWeekCompliancePct` read `completion_status === 'completed'`
 * from `plan_days`. Until this hook ships, that field is never
 * written from React → streak always 0 → `streak_milestone` and
 * `compliance_drop` triggers never fire. This PR lights up those
 * triggers with real data.
 *
 * **Optimistic update pattern:**
 *   1. Caller passes the current `completionStatus` along with the
 *      day's id + plan id.
 *   2. The hook returns a `toggle(dayId, currentStatus)` callback.
 *   3. On click, caller invokes the callback with optimistic UI
 *      update (e.g., `setLocalDays(prev => ...)`) — DO NOT wait for
 *      the PUT to settle before painting.
 *   4. The hook fires the PUT; on success, no-op (UI already shows
 *      the new state). On failure, the hook returns the error and
 *      the caller rolls back the optimistic update.
 *
 * **Why optimistic:** the network round-trip is ~200-500ms; a
 * synchronous toggle that waits feels broken on every tap.
 *
 * **Auth + ownership:** the backend validates `canAccessUser`
 * before accepting the write (api/index.js:3411). Hook trusts that
 * gate — no client-side ownership check.
 */

import { useCallback, useState } from 'react';
import { useAuthFetch } from './useAuthFetch';

export type PlanDayCompletionStatus = 'completed' | 'skipped' | 'upcoming';

export interface UsePlanDayCompletionArgs {
  /** `training_plans.id` for the week containing the day being
   *  toggled. From `useWeeklyPlan().planId`. */
  planId: string | null;
}

export interface UsePlanDayCompletionResult {
  /**
   * Toggle a plan_day's completion status. Returns a promise that
   * resolves with the new status on success, or rejects with an Error
   * on failure (caller rolls back optimistic UI in the catch).
   *
   * The toggle rule mirrors vanilla `plan-actions.js:19`:
   *   'completed' → 'upcoming'
   *   anything else → 'completed'
   *
   * (The 'skipped' state is a vanilla-parity DEFERRAL — vanilla has
   * `handleDaySkip()` to mark skipped explicitly, but this MVP only
   * exposes the binary toggle. Skipped state ships in a follow-up.)
   */
  toggle: (
    dayId: string,
    currentStatus: PlanDayCompletionStatus | null | undefined,
  ) => Promise<PlanDayCompletionStatus>;
  /**
   * RPE restore — persist a 1-10 perceived-effort rating for a day
   * (no completion-status change). Fired by the post-completion
   * RpePrompt; the backend PUT already accepts `rpe_value`.
   */
  saveRpe: (dayId: string, rpe: number) => Promise<void>;
  /**
   * AD-2.1 PR1 — persist the athlete's ACTUAL run distance for a day
   * (`actual_distance` + unit). Gated UI-side by the
   * `actual_distance_capture_v1` server flag; the backend independently
   * validates + rejects the fields when the flag is off, so a failed save
   * never corrupts the completion itself (fire-and-forget at the call site).
   */
  saveActualDistance: (dayId: string, value: number, unit: 'mi' | 'km') => Promise<void>;
  /**
   * F-110(a) — persist an RPE rating AND the actual distance in ONE PUT so the
   * capture sheet's single Save commits both atomically (the founder's loss
   * case: a chip tap used to commit RPE + close, discarding a typed distance).
   * Either field may be null (RPE-only or distance-only); an all-null call is a
   * no-op. Same `/api/plans/:planId/days/:dayId` path the backend already
   * accepts `rpe_value` + `actualDistance`/`actualDistanceUnit` on together.
   */
  saveRpeAndDistance: (
    dayId: string,
    rpe: number | null,
    distance: { value: number; unit: 'mi' | 'km' } | null,
  ) => Promise<void>;
  /** True while a PUT is in flight. Multiple concurrent toggles are
   *  allowed (the caller is responsible for debouncing if needed); this
   *  flag is the OR of all in-flight requests. */
  isPending: boolean;
  /** Last error from a failed PUT. Cleared on next successful toggle. */
  error: Error | null;
}

export function usePlanDayCompletion({
  planId,
}: UsePlanDayCompletionArgs): UsePlanDayCompletionResult {
  const fetchAuthed = useAuthFetch();
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);

  const toggle = useCallback(
    async (
      dayId: string,
      currentStatus: PlanDayCompletionStatus | null | undefined,
    ): Promise<PlanDayCompletionStatus> => {
      if (!planId) {
        const err = new Error(
          'usePlanDayCompletion.toggle called with null planId: no current-week plan to update.',
        );
        setError(err);
        throw err;
      }
      if (!dayId) {
        const err = new Error(
          'usePlanDayCompletion.toggle called with empty dayId.',
        );
        setError(err);
        throw err;
      }

      // Vanilla parity: only flip completed ↔ upcoming. A 'skipped'
      // input flips to 'completed' (matches `currentStatus !== 'completed'`).
      const nextStatus: PlanDayCompletionStatus =
        currentStatus === 'completed' ? 'upcoming' : 'completed';

      setPendingCount((c) => c + 1);
      try {
        const res = await fetchAuthed(`/api/plans/${planId}/days/${dayId}`, {
          method: 'PUT',
          // RPE restore — un-completing clears the day's effort rating
          // (vanilla plan-actions.js parity: rpeValue null on undo).
          body: JSON.stringify(
            nextStatus === 'completed'
              ? { completionStatus: nextStatus }
              : { completionStatus: nextStatus, rpe_value: null },
          ),
        });
        if (!res.ok) {
          throw new Error(
            `/api/plans/${planId}/days/${dayId} responded ${res.status}`,
          );
        }
        setError(null);
        return nextStatus;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setPendingCount((c) => Math.max(0, c - 1));
      }
    },
    [planId, fetchAuthed],
  );

  const saveRpe = useCallback(
    async (dayId: string, rpe: number): Promise<void> => {
      if (!planId || !dayId) {
        const err = new Error('usePlanDayCompletion.saveRpe called without planId/dayId.');
        setError(err);
        throw err;
      }
      setPendingCount((c) => c + 1);
      try {
        const res = await fetchAuthed(`/api/plans/${planId}/days/${dayId}`, {
          method: 'PUT',
          body: JSON.stringify({ rpe_value: rpe }),
        });
        if (!res.ok) {
          throw new Error(`/api/plans/${planId}/days/${dayId} responded ${res.status}`);
        }
        setError(null);
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setPendingCount((c) => Math.max(0, c - 1));
      }
    },
    [planId, fetchAuthed],
  );

  const saveActualDistance = useCallback(
    async (dayId: string, value: number, unit: 'mi' | 'km'): Promise<void> => {
      if (!planId || !dayId) {
        const err = new Error('usePlanDayCompletion.saveActualDistance called without planId/dayId.');
        setError(err);
        throw err;
      }
      setPendingCount((c) => c + 1);
      try {
        const res = await fetchAuthed(`/api/plans/${planId}/days/${dayId}`, {
          method: 'PUT',
          body: JSON.stringify({ actualDistance: value, actualDistanceUnit: unit }),
        });
        if (!res.ok) {
          throw new Error(`/api/plans/${planId}/days/${dayId} responded ${res.status}`);
        }
        setError(null);
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setPendingCount((c) => Math.max(0, c - 1));
      }
    },
    [planId, fetchAuthed],
  );

  const saveRpeAndDistance = useCallback(
    async (
      dayId: string,
      rpe: number | null,
      distance: { value: number; unit: 'mi' | 'km' } | null,
    ): Promise<void> => {
      if (!planId || !dayId) {
        const err = new Error('usePlanDayCompletion.saveRpeAndDistance called without planId/dayId.');
        setError(err);
        throw err;
      }
      // One atomic body: only the fields that are present. Nothing to write →
      // no request (a Save with neither RPE picked nor a valid distance).
      const body: Record<string, unknown> = {};
      if (rpe != null) body.rpe_value = rpe;
      if (distance != null) {
        body.actualDistance = distance.value;
        body.actualDistanceUnit = distance.unit;
      }
      if (Object.keys(body).length === 0) return;
      setPendingCount((c) => c + 1);
      try {
        const res = await fetchAuthed(`/api/plans/${planId}/days/${dayId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          throw new Error(`/api/plans/${planId}/days/${dayId} responded ${res.status}`);
        }
        setError(null);
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setPendingCount((c) => Math.max(0, c - 1));
      }
    },
    [planId, fetchAuthed],
  );

  return {
    toggle,
    saveRpe,
    saveActualDistance,
    saveRpeAndDistance,
    isPending: pendingCount > 0,
    error,
  };
}
